import { yupResolver } from '@hookform/resolvers/yup';
import { HttpStatusCode, isAxiosError } from 'axios';
import { Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useStoreSelector } from 'v3/data/store';
import { setNavigationStep } from 'v3/data/store/contractCard/actions';
import { ContractNavigationEnum } from 'v3/data/store/contractCard/enum';
import { setContractStepperState } from 'v3/data/store/contractCard/stepper/actions';
import { ContractStepperEnum } from 'v3/data/store/contractCard/stepper/enum';
import { setContractingState, setTokenValid } from 'v3/data/store/contracting/actions';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setSelectedOption, setTokenCode } from 'v3/data/store/token/action';
import { ITokenCodeType } from 'v3/data/store/token/types';
import { usePreferenceOptionActive } from 'v3/hooks/useIsPreferenceOptionActive';
import {
  BoxButtonStyled,
  BoxStyled,
  CardContentStyled,
  InputAndButtonValidationStyled,
  PaddingButton,
  SentTokenStyled,
} from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/Token/styles';
import * as api from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import Countdown from 'v3/shared/components/Countdown';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { TextMaskCustom } from 'v3/shared/components/Inputs/MaskCustomInput';
import { TokenFormatEnum } from 'v3/shared/constants/token-type.enum';
import { PreferencesOptionsEnum } from 'v3/shared/enums/preferences-options-enum';
import { tokenSchema } from 'v3/shared/schemas/token';
import { BodySmallRegular } from 'v3/shared/typography/BodySmall';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';

interface ITokenStep {
  onValidate: Function;
}
export const TokenStep = ({ onValidate }: ITokenStep) => {
  const { currentStep } = useStoreSelector((store) => store.navigate);
  const { selectedOption } = useStoreSelector((store) => store.token);
  const { contractingKey } = useStoreSelector((store) => store.contracting);
  const { canSendToken, code, flagSelectForPreContract } = useStoreSelector((store) => store.token);
  const isFlowDatasys = usePreferenceOptionActive(PreferencesOptionsEnum.IKCLOUD_40826_DATASYS_VOUCHER);

  const [count, setCount] = useState(0);
  const [tokenRequest, setTokenRequest] = useState(false);

  const {
    control,
    setError,
    formState: { isValid },
  } = useForm<ITokenCodeType>({
    mode: 'onChange',
    resolver: yupResolver(tokenSchema),
  });

  const handleSendToken = async (option: TokenFormatEnum) => {
    try {
      await api.generateToken(contractingKey, option);
      const now = new Date().getTime();
      setCount(now);
      setTokenRequest(true);
      setSelectedOption(option);
      setNavigationStep(ContractNavigationEnum.TOKEN_REQUESTED);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === HttpStatusCode.TooManyRequests) {
        const timeRecovered = error.response?.data?.details?.timeRecovered;
        setTokenRequest(true);
        setCount(timeRecovered);
        setNavigationStep(ContractNavigationEnum.TOKEN_REQUESTED);
        return;
      }

      showErrorDialog('Erro ao enviar o link', error);
    }
  };

  const handleValidateToken = async () => {
    try {
      await api.validateToken(contractingKey, code);
      setTokenValid(true);
      setNavigationStep(
        flagSelectForPreContract ? ContractNavigationEnum.PRE_CONTRACT : ContractNavigationEnum.TOKEN_VERIFIED,
      );
      const response = await api.getContractingState(contractingKey);
      setContractingState(response.data.status);
      if (isFlowDatasys) {
        onValidate();
      }
    } catch (error) {
      processValidationError(error);
    }
  };

  const processValidationError = (error) => {
    if (error.response?.status === HttpStatusCode.UnprocessableEntity) {
      setError('code', { type: 'manual', message: 'Token inválido. Por favor, tente novamente.' });
    } else {
      showErrorDialog('Erro ao validar o token', error);
    }
  };

  const advanceToDeviceInformation = () => {
    setContractStepperState({ stepName: ContractStepperEnum.TokenStep, error: false, isCompleted: true });
  };

  useEffect(() => {
    if (currentStep === ContractNavigationEnum.TOKEN_VERIFIED) {
      advanceToDeviceInformation();
    }
  }, [currentStep]);

  const handleOnChange = (value: string) => setTokenCode(value);

  return (
    <Fragment>
      {currentStep === ContractNavigationEnum.INITIAL && (
        <CardContentStyled>
          <BoxStyled>
            <BoxButtonStyled>
              <FlexButton
                data-testid="flex-button-sms-token"
                text="Enviar por SMS"
                size="small"
                disabled={!canSendToken}
                handleClick={() => handleSendToken(TokenFormatEnum.SMS)}
                option={TokenFormatEnum.SMS}
                selectValue={selectedOption}
              />
              <FlexButton
                data-testid="flex-button-email-token"
                text="Enviar por E-mail"
                size="small"
                disabled={!canSendToken}
                handleClick={() => handleSendToken(TokenFormatEnum.EMAIL)}
                option={TokenFormatEnum.EMAIL}
                selectValue={selectedOption}
              />
            </BoxButtonStyled>
          </BoxStyled>
        </CardContentStyled>
      )}

      {currentStep === ContractNavigationEnum.TOKEN_REQUESTED && tokenRequest && (
        <SentTokenStyled>
          <SubtitleLarge>Enviamos o código para o cliente</SubtitleLarge>
          <BodySmallRegular>
            O código já foi solicitado. Se o cliente não recebeu, ele poderá reenviar o código após o contador zerar
          </BodySmallRegular>
          <InputAndButtonValidationStyled>
            <InputFormController
              data-testid="input-code"
              inputType="tel"
              formControl={control}
              formControlName="code"
              label="Código de verificação"
              mask="000000"
              placeholder="123456"
              customMask={TextMaskCustom}
              fullWidth={false}
              onChangeInput={handleOnChange}
            />
            <PaddingButton>
              <FlexButton
                data-testid="flex-button-validar"
                text="Validar"
                size="small"
                disabled={!isValid}
                handleClick={handleValidateToken}
              />
            </PaddingButton>
          </InputAndButtonValidationStyled>
          <Countdown
            count={count}
            selectedMethod={selectedOption}
            onResend={() => handleSendToken(selectedOption!)}
            onChangeMethod={() => {
              setNavigationStep(ContractNavigationEnum.INITIAL);
              setTokenRequest(false);
            }}
          />
        </SentTokenStyled>
      )}
    </Fragment>
  );
};
